// Authentication settings
import { ref } from 'vue'
import { loadScriptAsync } from './helpers'
import { loadFriends } from './facebook'
import { useStore } from '../stores/index'
import { useUserStore } from '../stores/user'
import userAxios from './axios'
import { loadExternalAccountsConnectionStatus } from '../classes/user'
import { defaultErrorModal } from './modals'
import { Router } from 'vue-router'

export const lastError = ref();

// Setup function
async function setupOauth(app){
	const store = useStore()

	// Load external Apple script if "Sign in with Apple is enabled"
	if(store.settings.appleClientId){
		await loadScriptAsync('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js')

		// Setup Apple Id
		window.AppleID.auth.init({
			clientId: store.settings.appleClientId, // This is the service ID we created.
			redirectURI: 'https://' + window.location.hostname + '/api/user-side/auth/apple', // As registered along with our service ID
			state: (new Date()).toString(), // Any string of your choice that you may use for some logic. It's optional and you may omit it.
			usePopup: true, // Important if we want to capture the data apple sends on the client side.
		})
	}
}

async function authenticateOauth(provider: string, params = {
	collect: false
})
{
	const userStore = useUserStore()

	const initialConnectionStatus = userStore.connectionStatus[provider]
	userStore.connectionStatus[provider] = "connecting"

	try{
		switch(provider){
			case "facebook":
				const { loginWithFacebook } = useFacebookAuth()
				const { code } = await loginWithFacebook()

				// Connect server side
				await userAxios.post(getConnectUrlByProvider(provider), {
					code,
					collect: params?.collect
				})

				// Attempt to load user friends here
				await loadFriends()
				break;

			case "apple":
				const { authorization } = await window.AppleID.auth.signIn({ usePopup: true })

				await userAxios.post('/api/user-side/connectAppleUser', authorization)
				break;

			case "google": {
				const { loginWithGoogle } = useGoogleAuth()
				const { code } = await loginWithGoogle()
				
				// Connect server side
				await userAxios.post(getConnectUrlByProvider(provider), {
					code,
					collect: params?.collect
				})
				break;	
			}

			case "linkedin": {
				const { loginWithLinkedIn } = useLinkedInAuth()
				let { code } = await loginWithLinkedIn()
				
				// Connect server side
				await userAxios.post(getConnectUrlByProvider(provider), {
					code,
					collect: params?.collect
				})
				break;
			}
		}

		// Update all statuses since we've connected
		loadExternalAccountsConnectionStatus();
	} catch (e){
		userStore.connectionStatus[provider] = initialConnectionStatus
		throw e
	}
}

export function getConnectUrlByProvider(provider: string)
{
	switch(provider){
		case "facebook": return "/api/user-side/connectFacebookUser";
		case "google": return "/api/user-side/connectGoogleUser";
		case "linkedin": return "/api/user-side/connectLinkedInUser";
	}
}

function disconnectOauth(provider: string)
{
	const userStore = useUserStore()

	// Optimistic approach here. No need to wait for result. Assuming we're disconnected
	switch (provider) {
		case "linkedin": userAxios.post('/api/user-side/disconnectLinkedInUser'); break;
		case "google": userAxios.post('/api/user-side/disconnectGoogleUser'); break;
		case "facebook": userAxios.post('/api/user-side/disconnectFacebookUser'); break;
		case "apple": userAxios.post('/api/user-side/disconnectAppleUser'); break;
		default: throw Error("Unknown provider value (" + provider + ")")
	}

	userStore.connectionStatus[provider] = "disconnected"
}

/**
 * Manual Facebook OAuth connection
 * @returns 
 */
export function useFacebookAuth()
{
	const store = useStore()

	function loginWithFacebook()
	{
		return new Promise((resolve, reject) => {
			const url = new URL("https://www.facebook.com/v20.0/dialog/oauth")

			url.searchParams.set("client_id", store.settings.fbAppId)
			url.searchParams.set("redirect_uri", location.origin + '/oauth-callback.html')
			url.searchParams.set("state", 'facebook')

			// If the scope is provided, add it to the url
			if(store.settings.userFriendsEnabled){
				url.searchParams.set("scope", "user_friends")
			}

			// Open Facebook auth popup
			const popup = window.open(url.toString(), 'oauth', 'width=600,height=800')

			if (!popup) {
				reject(new Error('Failed to open popup'))
				return
			}

			const channel = new BroadcastChannel('oauth')
			channel.onmessage = (event) => {
				if(!event.data?.code){
					channel.close()
					reject(new Error('Popup closed before completion'))
				}

				if (event.data.provider === 'facebook') {
					channel.close()
					resolve(event.data)
				}
			}

			const timer = setInterval(() => {
				if (popup.closed) {
					clearInterval(timer)
					channel.close()
					reject(new Error('Popup closed before completion'))
				}
			}, 500)
		})
	}

	return { loginWithFacebook }
}

/**
 * Manual Google OAuth connection
 * @returns 
 */
export function useGoogleAuth()
{
	const store = useStore()

	function loginWithGoogle()
	{
		return new Promise((resolve, reject) => {
			const url = new URL("https://accounts.google.com/o/oauth2/v2/auth")

			url.searchParams.set("client_id", store.settings.googleId)
			url.searchParams.set("redirect_uri", location.origin + '/oauth-dummy.html')
			url.searchParams.set("response_type", "code")
			url.searchParams.set("scope", "openid email profile")
			url.searchParams.set("access_type", "offline")
			url.searchParams.set("prompt", "consent")

			const popup = window.open(url.toString(), 'oauth', 'width=600,height=800')

			if (!popup) {
				reject(new Error('Failed to open popup'))
				return
			}

			const channel = new BroadcastChannel('oauth')
			channel.onmessage = (event) => {
				if(!event.data?.code){
					channel.close()
					reject(new Error('Popup closed before completion'))
				}

				if (event.data.provider === 'google') {
					channel.close()
					resolve(event.data)
				}
			}

			const timer = setInterval(() => {
				if (popup.closed) {
					clearInterval(timer)
					channel.close()
					reject(new Error('Popup closed before completion'))
				}
			}, 500)
		})
	}

	return { loginWithGoogle }
}

/**
 * Manual Linkedin OAuth connection
 * @returns 
 */
export function useLinkedInAuth()
{
	const store = useStore()

	function loginWithLinkedIn() {
		return new Promise((resolve, reject) => {
			const url = new URL("https://www.linkedin.com/oauth/v2/authorization")

			url.searchParams.set("response_type", "code")
			url.searchParams.set("client_id", store.settings.linkedinKey)
			url.searchParams.set("redirect_uri", location.origin + '/oauth-dummy.html')
			url.searchParams.set("scope", "r_liteprofile r_emailaddress")

			const popup = window.open(url.toString(), 'oauth', 'width=600,height=800')

			if (!popup) {
				reject(new Error('Failed to open popup'))
				return
			}

			const channel = new BroadcastChannel('oauth')
			channel.onmessage = (event) => {
				if(!event.data?.code){
					channel.close()
					reject(new Error('Popup closed before completion'))
				}
				
				if (event.data.provider === 'linkedin') {
					channel.close()
					resolve(event.data)
				}
			}

			const timer = setInterval(() => {
				if (popup.closed) {
					clearInterval(timer)
					channel.close()
					reject(new Error('Popup closed before completion'))
				}
			}, 500)
		})
	}

	return { loginWithLinkedIn }
}

/**
 * Handle and process the login error
 * @param e 
 * @returns 
 */
export function processLoginError(e, provider: string, params = {
	router: {},
	route: {}
})
{
	const store = useStore()
	store.loaderContent = false

	if(e.message == 'Popup closed before completion' || !e.response){ // User just closed window
		return;
	}

	const lastError = e.response?.data?.error_code // UserNotExist, LinkedInUserNotLoggedIn, GoogleUserNotLoggedIn, FacebookUserNotLoggedIn

	if(lastError == "UserNotExist" && store.settings.registration_type == 'phone' && params?.route?.name == "phone-login"){
		params?.router?.push({name: 'registration'})
		return;
	}

	const t = store.translations // Make a shortcut
	let text = "";

	// Resolve texts of known erorrs
	switch(lastError){
		case "OtherUserIsConnectedWithSameExternalAccount": text = t.generalErrorSocialDuplicateAccount; break;
		case "OtherUserIsConnectedWithSameEmail": text = t.generalErrorSocialDuplicateAccount; break;
		case "UserNotExist": text = t.loginErrorUserNotExists; break;
		default: text = t.generalErrorTryLater + ".<br><small class='text-muted'>" + lastError + "</small>"; break;
	}

	defaultErrorModal(t.generalErrorLogin, {
		text
	})
}


/**
 * This method is used for signup and registration.
 */
export async function sendLoginSms(router: Router)
{
	const store = useStore()

	if((store.settings.turnstileKey && !store.signInData.turnstileToken) || !store.signInData.phoneNumber) return;

	store.smsInitiator = 'phone-login' // Cache to store

	var postData = {
		turnstileToken: store.signInData.turnstileToken,
		phone: store.signInData.phoneNumber.replace(/^\D/g, ""),
		name: store.signInData.name,
		surname: store.signInData.surname,
		lang: store.currentLanguage,
	}

	try {
		return await userAxios.post('/api/user-side/loginUserByPhone?phone', postData)
	} catch (e) {
		// Process error
		if (e.response?.data?.error_code) {
			lastError.value = e.response.data.error_code
		} else if (e.request) {
			console.log('Cannot send login SMS', e.request)
		}

		// Special case of exception, redirects to the waitlist page
		if (e.response?.data?.error_code == "CountryPhonePrefixIsNotAllowed") {
			router.push({ path: 'waitlist-invitation' })
		}

		throw e
	}
}

export default {}

export {
	setupOauth,
	authenticateOauth,
	disconnectOauth,
}